import { type SubmitEventHandler, useEffect, useState } from 'react';
import Sparkles from '@/assets/sparkles.svg';
import XLight from '@/assets/xLight.svg';
import CheckboxCircleIcon from '@/assets/checkboxCircle.svg';
import ArrowRightLight from '@/assets/arrowRightLight.svg';
import Modal from '@/components/general/Modal';
import type { AnalyticsBuilderInputs, AnalyticsBuilderResolvedTemplateType } from '@/types';
import { AnalyticsBuilderFieldTypeType } from '@/types';
import AnalyticsBuilderSetupField from './AnalyticsBuilderSetupField';

type FieldValues = Record<string, string>;
type FieldErrors = Record<string, boolean>;
type FieldErrorMessages = Record<string, string>;

const missingAutocompleteSelectionErrorMessage =
  'Please choose a value from the list of suggestions, not a typed-only value.';

const autocompleteFieldTypes = [
  AnalyticsBuilderFieldTypeType.AutocompleteCity,
  AnalyticsBuilderFieldTypeType.AutocompleteGeography,
  AnalyticsBuilderFieldTypeType.AutocompleteCompany,
];

const getAutocompleteValueFieldName = (fieldName: string) =>
  fieldName.endsWith('Id') ? fieldName.replace(/Id$/, 'Name') : fieldName.replace(/Name$/, 'Id');

type AnalyticsBuilderSetupModalProps = {
  open: boolean;
  template: AnalyticsBuilderResolvedTemplateType | null;
  inputs: AnalyticsBuilderInputs | null;
  onClose: () => void;
  onGenerate: (inputs: AnalyticsBuilderInputs) => void;
};

const AnalyticsBuilderSetupModal = ({
  open,
  template,
  inputs,
  onClose,
  onGenerate,
}: AnalyticsBuilderSetupModalProps) => {
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [fieldErrorMessages, setFieldErrorMessages] = useState<FieldErrorMessages>({});
  const [shakeFields, setShakeFields] = useState<FieldErrors>({});

  useEffect(() => {
    if (!open || !template) {
      return;
    }

    setFieldValues(
      Object.entries(inputs || {}).reduce<FieldValues>((values, [key, value]) => {
        values[key] = value || '';
        return values;
      }, {}),
    );
    setFieldErrors({});
    setFieldErrorMessages({});
    setShakeFields({});
  }, [open, template, inputs]);

  const handleSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const fields = template?.fieldSet?.fields || [];
    const nextValues = fields.reduce<FieldValues>((values, field) => {
      values[field.queryParameterName] = (fieldValues[field.queryParameterName] || '').trim();
      return values;
    }, {});
    const nextErrors = fields.reduce<FieldErrors>((errors, field) => {
      if (field.type === AnalyticsBuilderFieldTypeType.Hidden) {
        errors[field.queryParameterName] = false;
        return errors;
      }

      errors[field.queryParameterName] = !nextValues[field.queryParameterName];
      return errors;
    }, {});
    const nextErrorMessages: FieldErrorMessages = {};

    fields.forEach((field) => {
      if (!autocompleteFieldTypes.includes(field.type)) {
        return;
      }

      const valueFieldName = getAutocompleteValueFieldName(field.queryParameterName);

      if (nextValues[field.queryParameterName] && !nextValues[valueFieldName]) {
        nextErrors[field.queryParameterName] = true;
        nextErrorMessages[field.queryParameterName] = missingAutocompleteSelectionErrorMessage;
      }
    });

    if (Object.values(nextErrors).some(Boolean)) {
      setFieldErrors(nextErrors);
      setFieldErrorMessages(nextErrorMessages);
      setShakeFields({});
      window.requestAnimationFrame(() => setShakeFields(nextErrors));
      return;
    }

    onGenerate(nextValues);
  };

  const handleFieldChange = (fieldName: string, value: string, optionValue?: string) => {
    if (optionValue !== undefined) {
      setFieldValues((current) => ({
        ...current,
        [fieldName]: value,
        [getAutocompleteValueFieldName(fieldName)]: optionValue,
      }));
    } else {
      setFieldValues((current) => ({ ...current, [fieldName]: value }));
    }

    setFieldErrorMessages((current) => ({ ...current, [fieldName]: '' }));

    if (value.trim()) {
      setFieldErrors((current) => ({ ...current, [fieldName]: false }));
    }
  };

  const handleFieldShakeEnd = (fieldName: string) => {
    setShakeFields((current) => ({ ...current, [fieldName]: false }));
  };

  if (!template) {
    return null;
  }

  const fields = template.fieldSet?.fields || [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      contanerClassName="max-h-[92vh] w-full max-w-11/12 xl:max-w-5xl overflow-hidden bg-white shadow-2xl"
    >
      <div className="flex items-center justify-between border-b border-zinc-100 py-5 pr-6 pl-4">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="btn icon">
            <ArrowRightLight className="h-7 w-7 rotate-180" />
          </button>
          <div>
            <p className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">Analytics Builder</p>
            <h2 className="text-xl font-semibold text-zinc-950">{template.displayName}</h2>
          </div>
        </div>
        <button onClick={onClose} className="btn icon">
          <XLight className="h-7 w-7" />
        </button>
      </div>

      <div
        className="grid max-h-[calc(92vh-85px)] grid-cols-1 overflow-y-auto lg:grid-cols-[420px_1fr]"
        data-lenis-prevent
      >
        <div className="border-r border-zinc-100 bg-zinc-50/70 p-6">
          <h3 className="mb-2 text-2xl font-semibold text-zinc-950">Set up your report</h3>
          <div
            className="mb-6 text-sm leading-6 text-zinc-600"
            dangerouslySetInnerHTML={{ __html: template.inputDescription || '' }}
          />

          <form className="space-y-5" onSubmit={handleSubmit} noValidate>
            {fields.map((field) => (
              <AnalyticsBuilderSetupField
                key={field.queryParameterName}
                field={field}
                onChange={handleFieldChange}
                onShakeEnd={handleFieldShakeEnd}
                errorMessage={fieldErrorMessages[field.queryParameterName]}
                shakeField={Boolean(shakeFields[field.queryParameterName])}
                showError={Boolean(fieldErrors[field.queryParameterName])}
                template={template}
                value={fieldValues[field.queryParameterName] || ''}
              />
            ))}

            <button type="submit" className="btn primary w-full hover:!bg-black/80 hover:!text-white">
              Generate report
              <Sparkles className="h-4.5 w-4.5" />
            </button>
          </form>
        </div>

        <div className="p-8">
          <div className="mx-auto max-w-2xl rounded-[6px] border border-zinc-100 bg-white p-8 shadow-sm">
            <p className="mb-3 text-sm font-semibold text-zinc-500">Report preview</p>
            <h3 className="mb-3 text-3xl font-semibold tracking-tight text-zinc-950">{template.displayName}</h3>
            <div className="mb-7 leading-7 text-zinc-600" dangerouslySetInnerHTML={{ __html: template.description }} />
            <div className="rounded-[6px] bg-zinc-50 p-5">
              <p className="mb-4 text-sm font-semibold text-zinc-800">Report structure</p>
              <div className="space-y-3 text-sm text-zinc-600">
                {template.areas.map((item) => (
                  <div key={item.id} className="flex items-center gap-2">
                    <CheckboxCircleIcon className="h-4 w-4" /> {item.displayName}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AnalyticsBuilderSetupModal;
